import { useState, useEffect } from 'react'
import { Trophy, Loader2, RotateCcw } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'

/* Free-For-All heat — één score per deelnemer, geen 1v1 winner.
   Rangorde binnen de heat = sort op score desc; placement wordt backend-zijde
   herbepaald bij het opslaan. Bij reset: alle scores op 0 en status → pending. */
export default function FFAScoreDialog({ open, onClose, match, onSubmit, loading }) {
  const [scores, setScores] = useState([])   // [{ participant_id, name, score }]
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!match) return
    setError(null)
    const rows = (match.deathmatch_scores || []).map(s => ({
      participant_id: s.participant_id,
      name: s.team_name || s.username || `#${s.participant_id}`,
      score: s.score ?? 0,
    }))
    setScores(rows)
  }, [match])

  if (!match) return null

  const setScore = (pid, v) => {
    const n = v === '' ? '' : Math.max(0, parseInt(v, 10) || 0)
    setScores(prev => prev.map(r => r.participant_id === pid ? { ...r, score: n } : r))
  }

  const gesorteerd = [...scores].sort((a, b) => Number(b.score || 0) - Number(a.score || 0))

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    if (scores.some(r => r.score === '')) { setError('Vul alle scores in (0 mag)'); return }
    try {
      await onSubmit(scores.map(r => ({ participant_id: r.participant_id, score: Number(r.score) })))
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-acid" />
            FFA-heat resultaten
          </DialogTitle>
          <DialogDescription>
            Vul één score per deelnemer in. De heat-plaatsing volgt automatisch uit
            de hoogste score.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            {scores.map((r) => {
              const isLeader = gesorteerd[0]?.participant_id === r.participant_id && Number(r.score) > 0
              return (
                <div key={r.participant_id}
                     className={cn('flex items-center gap-3 p-3 rounded-lg border',
                                   isLeader ? 'border-live/60 bg-live/10' : 'border-border')}>
                  <div className="flex-1 min-w-0">
                    <div className={cn('font-semibold truncate', isLeader && 'text-live')}>{r.name}</div>
                  </div>
                  <Input type="number" min="0" value={r.score}
                         onChange={e => setScore(r.participant_id, e.target.value)}
                         placeholder="0"
                         className="w-24 text-right tabular-nums text-lg font-bold" />
                </div>
              )
            })}
          </div>

          {error && (
            <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          )}

          <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
            {match.status === 'completed' && (
              <Button type="button" variant="outline"
                      className="text-signal border-signal/40 hover:border-signal"
                      disabled={loading}
                      onClick={async () => {
                        if (!confirm('Heat resetten? Alle scores worden op 0 gezet en de heat komt terug op "wachten".')) return
                        try {
                          await onSubmit(scores.map(r => ({ participant_id: r.participant_id, score: 0 })), { reset: true })
                          onClose()
                        } catch (err) {
                          setError(err.response?.data?.error || 'Reset mislukt')
                        }
                      }}>
                <RotateCcw className="h-4 w-4 mr-1" /> Reset
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Annuleren
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Scores opslaan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
